import { Copy07Icon } from '@/components/icons/Copy07Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  MenuItem,
  MenuItemProps,
} from '@/components/poppers/MenuPopper/MenuItem';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

import { DocumentsTableRowData } from '../DocumentsTable.types';
// generated GraphQL hook
import { useCopyInferredDataActionItemLazyQuery } from './graphql/CopyInferredDataActionItem.generated';

interface CopyInferredDataActionItemProps extends Partial<MenuItemProps> {
  row: DocumentsTableRowData;
}

/**
 * @description Action item for copying inferred data for a document to the clipboard.
 */
export function CopyInferredDataActionItem({
  row,
  ...menuItemProps
}: CopyInferredDataActionItemProps) {
  const [copyToClipboard] = useCopyToClipboard();
  const { createErrorFeedback, showFeedback } = useFeedback();

  const [copyInferredData, { loading }] =
    useCopyInferredDataActionItemLazyQuery({
      variables: {
        documentId: row.id,
      },
      onError: createErrorFeedback('Failed to copy inferred data'),
      onCompleted: (data) => {
        // Guard against missing data
        const documentNode = data?.documents?.edges?.[0]?.node ?? null;

        if (!documentNode) {
          return showFeedback('No inferred data found');
        }

        const questions =
          documentNode.questions?.edges?.flatMap((edge) =>
            edge?.node ? [edge.node] : []
          ) ?? [];

        if (questions.length === 0) {
          return showFeedback('No inferred data found');
        }

        const inferredData = {
          documentId: documentNode.id,
          questions: questions.map((q) => ({
            id: q.id,
            kind: q.kind ?? undefined,
            answers: q.answers?.map((a) => a.answer).filter(Boolean) ?? [],
          })),
        };

        return copyToClipboard(
          JSON.stringify(inferredData, null, 2),
          () =>
            showFeedback('Inferred data copied to clipboard', {
              variant: 'success',
            }),
          () => showFeedback('Failed to copy to clipboard')
        );
      },
    });

  return (
    <MenuItem
      icon={<Copy07Icon size={18} />}
      loading={loading}
      label="Copy inferred data"
      onClick={() => copyInferredData()}
      {...menuItemProps}
    />
  );
}
